import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AuthService } from 'ionic-appauth';
import { CapacitorBrowser, CapacitorSecureStorage } from 'ionic-appauth/lib/capacitor';

import type { CapacitorConfig } from '@capacitor/cli';

import Config from 'Config';
import { CapacitorRequestor } from './CapacitorRequestor';

export abstract class OktaAuthBase {
    protected constructor() {} /* eslint-disable-line no-useless-constructor, no-empty-function -- This is supposed to be used as a singleton by subclasses, protected so they can extend the class but not public so that it can't be instantiated. */

    /**
     * Protected so that subclasses can call it but can't be accessed directly. Should be accessed through a `public
     * static async getInstance()` that caches the singleton.
     *
     * @example
     *  export class OktaExternalAuth extends OktaAuthBase {
     *      private static authService: AuthService | undefined;
     *
     *      protected static async buildAuthInstance() {
     *          return super.buildAuthInstance({
     *              capacitorConfig: CapacitorConfig,
     *              config: {
     *                  oktaClientId: Config.oktaExternalClientId,
     *                  oktaServerHostUrl: Config.oktaExternalServerHostUrl,
     *                  oktaSignInRedirectPath: Config.oktaExternalSignInRedirectPath,
     *                  oktaSignOutRedirectPath: Config.oktaExternalSignOutRedirectPath,
     *              },
     *          });
     *      }
     *
     *      public static async getInstance() {
     *          if (!this.authService) {
     *              this.authService = await this.buildAuthInstance();
     *          }
     *
     *          return this.authService;
     *      }
     *  }
     */
    protected static async buildAuthInstance({
        capacitorConfig,
        config,
    }: {
        capacitorConfig: CapacitorConfig;
        config: {
            oktaSignInRedirectPath: string;
            oktaSignOutRedirectPath: string;
            oktaClientId: string;
            oktaServerHostUrl: string;
        };
    }) {
        const platform = Capacitor.getPlatform();
        const isMobile = platform !== 'web';
        const redirectUrl = isMobile
            ? `${capacitorConfig.appId}:/${config.oktaSignInRedirectPath}`
            : `${Config.paytronURL || ''}${config.oktaSignInRedirectPath}`;
        const endSessionUrl = isMobile
            ? `${capacitorConfig.appId}:/${config.oktaSignOutRedirectPath}`
            : `${Config.paytronURL || ''}${config.oktaSignOutRedirectPath}`;

        const authService = new AuthService(
            new CapacitorBrowser(),
            new CapacitorSecureStorage(),
            new CapacitorRequestor()
        );

        authService.authConfig = {
            client_id: config.oktaClientId,
            server_host: config.oktaServerHostUrl,
            redirect_url: redirectUrl,
            end_session_redirect_url: endSessionUrl,
            scopes: 'openid profile offline_access ALLAPI',
            pkce: true,
        };

        if (isMobile) {
            await App.addListener('appUrlOpen', (data: { url: string }) => {
                const pathName = data.url.replace(`${capacitorConfig.appId}:/`, '');
                if (data.url.indexOf(authService.authConfig.redirect_url) === 0) {
                    window.location.href = pathName;
                } else {
                    window.location.href = config.oktaSignOutRedirectPath;
                }
            });
        }

        await authService.init();
        return authService;
    }
}
